import {useEffect, useState} from "react";
import CustomLoader from "../../CustomComponents/CustomLoader";

import {useLocation} from "react-router-dom";
import {GetActivityListService} from "../../services/contractorServices";

import Accordion from "@mui/material/Accordion";
import AccordionActions from "@mui/material/AccordionActions";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";

import Rating from "@mui/material/Rating";
import CustomUserName from "../../CustomComponents/CustomUserName";
import {fDate} from "../../utils/format-time";
import {truncateWords} from "../../utils/format-text";

const MyActivity = () => {
	const location = useLocation();

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	const [loading, setLoading] = useState(true);
	const [list, setList] = useState([]);
	const [addressList, setAddressList] = useState([]);

	const [filterStatus, setFilterStatus] = useState(location.state?.filter || "all");

	// Modal states
	const [likesModalOpen, setLikesModalOpen] = useState(false);
	const [upliftsModalOpen, setUpliftsModalOpen] = useState(false);
	const [selectedLikes, setSelectedLikes] = useState([]);
	const [selectedUplifts, setSelectedUplifts] = useState([]);
	const [modalTitle, setModalTitle] = useState("");
	const [selectedActivity, setSelectedActivity] = useState(null);

	const getMockRatings = async () => {
		try {
			setLoading(true);
			const response = await GetActivityListService({
				pagesize: pageSize,
				page: page,
			});

			if (response?.status) {
				setList(response?.data?.list);
				setAddressList(response?.data?.listAddress);
			}
		} catch (error) {
			console.error("Error fetching activity list:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getMockRatings();
	}, [page, pageSize]);

	// Calculate total activities count
	const getTotalActivities = () => {
		return list.reduce((total, item) => total + (item?.list?.length || 0), 0);
	};

	// Calculate average rating for an address
	const getAverageRating = (addressList) => {
		const ratings = addressList?.filter((item) => item?.rating_id && item?.stars);
		if (ratings.length === 0) return 0;
		const sum = ratings.reduce((acc, item) => acc + item.stars, 0);
		return sum / ratings.length;
	};

	// Format date
	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	// Format date and time
	const formatDateTime = (dateString) => {
		return new Date(dateString).toLocaleString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: true,
		});
	};

	// Format job issues
	const formatJobIssue = (issue) => {
		return issue.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
	};

	// Filter list based on selected address
	const filteredList = filterStatus === "all" ? list : list.filter((item) => item.title === filterStatus);

	// Handle likes modal
	const handleShowLikes = (likes, title, activity) => {
		setSelectedLikes(likes || []);
		setModalTitle(`${title} - Likes`);
		setSelectedActivity(activity);
		setLikesModalOpen(true);
	};

	// Handle uplifts modal
	const handleShowUplifts = (uplifts, title, activity) => {
		setSelectedUplifts(uplifts || []);
		setModalTitle(`${title} - Uplifts`);
		setSelectedActivity(activity);
		setUpliftsModalOpen(true);
	};

	// Get user initials for avatar
	const getUserInitials = (firstName, lastName) => {
		return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
	};

	// Modal styles
	const modalStyle = {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: 600,
		bgcolor: "background.paper",
		border: "2px solid #000",
		boxShadow: 24,
		p: 4,
		borderRadius: 2,
		maxHeight: "80vh",
		overflow: "auto",
	};

	return (
		<div className="dashboard-page">
			<div className="page-header">
				<h2>My Activity</h2>
				<p>View and manage all your submitted ratings and comments ({getTotalActivities()} total)</p>
			</div>

			<div className="ratings-controls">
				<div className="filter-section">
					<select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All Addresses</option>
						{addressList &&
							addressList?.map((item, index) => (
								<option key={index} value={item?.full_address}>
									{truncateWords(item?.full_address, 40)}
								</option>
							))}
					</select>
				</div>
			</div>

			{loading ? (
				<div className="ratings-container">
					<CustomLoader />
				</div>
			) : (
				<div className="">
					{filteredList && filteredList.length > 0 ? (
						filteredList.map((item, index) => {
							const averageRating = getAverageRating(item.list);
							const ratingsCount = item.list.filter((activity) => activity?.rating_id).length;
							const commentsCount = item.list.filter((activity) => activity?.street_id).length;

							return (
								<Accordion key={index} defaultExpanded>
									<AccordionSummary aria-controls={`panel${index}-content`} id={`panel${index}-header`} sx={{borderBottom: "solid 1px #CCC"}}>
										<div className="d-flex justify-content-between align-items-center w-100">
											<div className="d-flex flex-column">
												<Typography component="span" variant="h6" className="fw-bold">
													{item?.title}
												</Typography>
												<Typography variant="body2" className="text-muted">
													{ratingsCount} ratings, {commentsCount} comments
												</Typography>
											</div>

											<Box sx={{display: "flex", alignItems: "center"}} gap={1.5}>
												{averageRating > 0 && (
													<>
														<Typography variant="h6" className="fw-bold">
															{averageRating.toFixed(1)}
														</Typography>
														<Rating value={averageRating} precision={0.1} readOnly size="small" />
													</>
												)}
											</Box>
										</div>
									</AccordionSummary>
									<AccordionDetails>
										<div>
											<div className="row">
												{item?.list?.length > 0 ? (
													item.list.map((activity, key) => {
														if (activity?.rating_id) {
															return (
																<div key={key} className="col-12 mb-3">
																	<div className={`card ${activity?.selfCreate === 1 ? "border-primary border-2" : "border-0"} shadow-sm ${activity?.selfCreate === 1 ? "bg-light" : ""}`}>
																		{activity?.selfCreate === 1 && (
																			<div className="card-header bg-primary text-white py-2">
																				<div className="d-flex align-items-center">
																					<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="me-2">
																						<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
																					</svg>
																					<span className="fw-semibold">Your Rating</span>
																				</div>
																			</div>
																		)}
																		<div className="card-body py-3">
																			<div className="row align-items-center">
																				<div className="col-md-3">
																					<div className="d-flex align-items-center mb-2">
																						<Rating value={activity?.stars} precision={0.5} readOnly />
																						<span className="ms-2 fw-bold">{activity?.stars}/5</span>
																					</div>
																					<div className="text-muted small">Job Date: {fDate(activity?.job_date)}</div>
																				</div>

																				<div className="col-md-6">
																					{activity?.job_issues && activity?.job_issues.length > 0 ? (
																						<div>
																							<div className="text-muted small mb-1">Job Issues:</div>
																							<div className="d-flex flex-wrap gap-1">
																								{activity.job_issues.map((issue, issueIndex) => (
																									<Chip label={formatJobIssue(issue)} size="small" />
																								))}
																							</div>
																						</div>
																					) : (
																						<div className="text-muted small">No issues reported</div>
																					)}
																				</div>

																				<div className="col-md-3">
																					<div className="text-end">
																						<div className="d-flex justify-content-end ">
																							<CustomUserName user={activity?.user} sx={{fontSize: 14, fontWeight: 600}} code />
																						</div>
																						<div className="d-flex justify-content-end gap-2 mt-1">
																							<Chip
																								label={`${activity?.likes?.length || 0} Likes`}
																								size="small"
																								color="primary"
																								onClick={() => handleShowLikes(activity?.likes, `${activity?.client_name} Rating`, activity)}
																							/>
																							{activity?.uplifts?.length > 0 && (
																								<Chip
																									label={`${activity?.uplifts?.length} Uplifts`}
																									size="small"
																									color="info"
																									onClick={() => handleShowUplifts(activity?.uplifts, `${activity?.client_name} Rating`, activity)}
																								/>
																							)}
																						</div>
																					</div>
																				</div>
																			</div>

																			{/* Combined Likes and Uplifts List */}
																			{(activity?.likes?.length > 0 || activity?.uplifts?.length > 0) && (
																				<div className="mt-3 pt-3 border-top">
																					<div className="d-flex justify-content-between align-items-center mb-2">
																						<div className="text-muted small fw-semibold">
																							Likes ({activity?.likes?.length || 0}) | Uplifts ({activity?.uplifts?.length || 0})
																						</div>
																					</div>
																					<div className="interactions-list">
																						{[...(activity?.likes || []).map((like) => ({...like, type: "like"})), ...(activity?.uplifts || []).map((uplift) => ({...uplift, type: "uplift"}))]
																							.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
																							// .slice(0, 5)
																							.map((interaction, interactionIndex) => (
																								<div key={interactionIndex} className="d-flex align-items-center py-1 px-2 mb-1 bg-light rounded">
																									<div className="me-2">
																										{interaction.type === "like" ? (
																											<span className="text-success fw-bold">
																												<Avatar variant="rounded" sx={{bgcolor: "#22c55e1a", color: "#28a745"}}>
																													{/* <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																														<path
																															d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.5783 8.50903 2.9987 7.05 2.9987C5.59096 2.9987 4.19169 3.5783 3.16 4.61C2.1283 5.6417 1.5487 7.04097 1.5487 8.5C1.5487 9.95903 2.1283 11.3583 3.16 12.39L12 21.23L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
																															stroke="currentColor"
																															strokeWidth="2"
																															strokeLinecap="round"
																															strokeLinejoin="round"
																														/>
																													</svg> */}
																													<div className="bi-hand-thumbs-up" style={{fontSize: 20}} />
																												</Avatar>
																											</span>
																										) : (
																											<Avatar variant="rounded" sx={{bgcolor: "#FFC1071A", color: "#FFC107"}}>
																												<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																													<path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																												</svg>
																											</Avatar>
																										)}
																									</div>
																									<div className="flex-grow-1">
																										<div className=" ">
																											<CustomUserName user={interaction?.user} sx={{fontWeight: 600}} code />
																										</div>
																										<div className="text-muted" style={{fontSize: "0.65rem"}}>
																											{formatDateTime(interaction?.createdAt)}
																										</div>
																									</div>
																									{interaction.type === "uplift" && (
																										<div className="me-2">
																											<Rating value={interaction?.stars} precision={0.5} readOnly size="small" sx={{fontSize: "0.6rem"}} />
																											<span className="fw-bold small ms-1">{interaction?.stars}/5</span>
																										</div>
																									)}
																									{interaction?.comment && (
																										<div className="text-muted small ms-2" style={{fontSize: "0.65rem"}}>
																											{interaction.comment.substring(0, 30)}...
																										</div>
																									)}
																									{interaction?.description && (
																										<div className="text-muted small ms-2" style={{fontSize: "0.65rem"}}>
																											{interaction.description.substring(0, 30)}...
																										</div>
																									)}
																								</div>
																							))}
																						{/* {activity?.likes?.length + activity?.uplifts?.length > 5 && (
																							<div className="text-center py-1">
																								<div className="text-muted small">+{activity?.likes?.length + activity?.uplifts?.length - 5} more interactions</div>
																							</div>
																						)} */}
																					</div>
																				</div>
																			)}
																		</div>
																	</div>
																</div>
															);
														}

														if (activity?.street_id) {
															return (
																<div key={key} className="col-12 mb-3">
																	<div className={`card ${activity?.selfCreate === 1 ? "border-success border-2" : "border-0"} shadow-sm ${activity?.selfCreate === 1 ? "bg-light" : ""}`}>
																		{activity?.selfCreate === 1 && (
																			<div className="card-header bg-success text-white py-2">
																				<div className="d-flex align-items-center">
																					<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="me-2">
																						<path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
																					</svg>
																					<span className="fw-semibold">Your Comment</span>
																				</div>
																			</div>
																		)}
																		<div className="card-body py-3">
																			<div className="row align-items-center">
																				<div className="col-md-9">
																					<div className="mb-2">
																						<strong>Comment:</strong>
																					</div>
																					<div className="text-muted">{activity?.description || "No description provided"}</div>
																				</div>
																				<div className="col-md-3">
																					<div className="text-end">
																						<div className="text-muted small mb-1">Job Date: {fDate(activity?.comment_date)}</div>
																						<div className="d-flex justify-content-end ">
																							<CustomUserName user={activity?.user} sx={{fontSize: 14, fontWeight: 600}} />
																						</div>
																						<div className="mt-1">
																							<Chip
																								label={`${activity?.likes?.length || 0} Likes`}
																								size="small"
																								color="primary"
																								onClick={() => handleShowLikes(activity?.likes, `${activity?.client_name} Comment`, activity)}
																								clickable
																							/>
																						</div>
																					</div>
																				</div>
																			</div>

																			{/* Likes List for Comments */}
																			{activity?.likes && activity?.likes.length > 0 && (
																				<div className="mt-3 pt-3 border-top">
																					<div className="d-flex justify-content-between align-items-center mb-2">
																						<div className="text-muted small fw-semibold">Likes ({activity.likes.length})</div>
																						{/* <Button size="small" variant="outlined" onClick={() => handleShowLikes(activity?.likes, `${activity?.client_name} Comment`, activity)}>
																							View All
																						</Button> */}
																					</div>
																					<div className="likes-list">
																						{activity.likes.map((like, likeIndex) => (
																							<div key={likeIndex} className="d-flex align-items-center py-1 px-2 mb-1 bg-light rounded">
																								<div className="me-2">
																									<Avatar variant="rounded" sx={{bgcolor: "#22c55e1a", color: "#28a745"}}>
																										{/* <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																											<path
																												d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.5783 8.50903 2.9987 7.05 2.9987C5.59096 2.9987 4.19169 3.5783 3.16 4.61C2.1283 5.6417 1.5487 7.04097 1.5487 8.5C1.5487 9.95903 2.1283 11.3583 3.16 12.39L12 21.23L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
																												stroke="currentColor"
																												strokeWidth="2"
																												strokeLinecap="round"
																												strokeLinejoin="round"
																											/>
																										</svg> */}

																										<div className="bi-hand-thumbs-up" style={{fontSize: 20}} />
																									</Avatar>
																								</div>
																								<div className="flex-grow-1">
																									<div className="fw-semibold small">
																										<CustomUserName user={like?.user} sx={{fontWeight: 600}} code />
																									</div>
																									<div className="text-muted" style={{fontSize: "0.65rem"}}>
																										{formatDateTime(like?.createdAt)}
																									</div>
																								</div>
																							</div>
																						))}
																						{/* {activity.likes.length > 5 && (
																							<div className="text-center py-1">
																								<div className="text-muted small">+{activity.likes.length - 5} more likes</div>
																							</div>
																						)} */}
																					</div>
																				</div>
																			)}
																		</div>
																	</div>
																</div>
															);
														}

														return null;
													})
												) : (
													<div className="col-12 text-center py-4">
														<div className="text-muted">No activities found for this address</div>
													</div>
												)}
											</div>
										</div>
									</AccordionDetails>
								</Accordion>
							);
						})
					) : (
						<div className="text-center py-5">
							<div className="text-muted">No activities found</div>
						</div>
					)}
				</div>
			)}

			{/* <Modal open={likesModalOpen} onClose={() => setLikesModalOpen(false)} aria-labelledby="likes-modal-title"> */}
			<Modal open={false} onClose={() => setLikesModalOpen(false)} aria-labelledby="likes-modal-title">
				<Box sx={modalStyle}>
					<Typography id="likes-modal-title" variant="h6" component="h2" className="mb-3">
						{modalTitle}
					</Typography>

					{selectedActivity && (
						<Card className="mb-3" variant="outlined">
							<CardHeader title="Original Content" subheader={selectedActivity?.rating_id ? "Rating" : "Comment"} size="small" />
							<CardContent>
								{selectedActivity?.rating_id ? (
									<div>
										<div className="d-flex align-items-center mb-2">
											<Rating value={selectedActivity?.stars} precision={0.5} readOnly size="small" />
											<span className="ms-2 fw-bold">{selectedActivity?.stars}/5</span>
										</div>
										<div className="text-muted small">
											Client: {selectedActivity?.client_name} | Job Date: {formatDate(selectedActivity?.job_date)}
										</div>
										{selectedActivity?.job_issues && selectedActivity?.job_issues.length > 0 && (
											<div className="mt-2">
												<div className="text-muted small mb-1">Issues:</div>
												<div className="d-flex flex-wrap gap-1">
													{selectedActivity.job_issues.slice(0, 3).map((issue, index) => (
														<Chip key={index} label={formatJobIssue(issue)} size="small" color="warning" />
													))}
													{selectedActivity.job_issues.length > 3 && <Chip label={`+${selectedActivity.job_issues.length - 3} more`} size="small" variant="outlined" />}
												</div>
											</div>
										)}
									</div>
								) : (
									<div>
										<div className="text-muted small mb-1">Comment:</div>
										<div>{selectedActivity?.description || "No description provided"}</div>
										<div className="text-muted small mt-1">Date: {formatDate(selectedActivity?.comment_date)}</div>
									</div>
								)}
							</CardContent>
						</Card>
					)}

					{/* Combined Interactions List */}
					{selectedLikes.length > 0 ? (
						<div>
							<Typography variant="subtitle1" className="mb-3 fw-bold">
								All Interactions ({selectedLikes.length})
							</Typography>
							<List>
								{selectedLikes.map((interaction, index) => (
									<ListItem key={index} className="px-0 mb-2">
										<div className="w-100">
											<div className="d-flex align-items-start">
												<div className="me-3">{interaction.type === "like" ? <span className="text-primary fw-bold fs-4">Like</span> : <span className="text-success fw-bold fs-4">uplift</span>}</div>
												<div className="flex-grow-1">
													<div className="d-flex justify-content-between align-items-center mb-1">
														<div>
															<span className="fw-bold">
																<CustomUserName user={interaction?.user} sx={{fontSize: 14, fontWeight: 700}} code />
															</span>
															{interaction?.user?.verified && <Chip label="Verified" size="small" color="success" className="ms-2" />}
														</div>
														<div className="text-muted small">{formatDateTime(interaction?.createdAt)}</div>
													</div>
													<div className="text-muted small mb-1">{interaction?.user?.email}</div>

													{/* Uplift Rating */}
													{interaction.type === "uplift" && (
														<div className="d-flex align-items-center gap-2 mb-2">
															<Rating value={interaction?.stars} precision={0.5} readOnly size="small" />
															<span className="fw-bold">{interaction?.stars}/5 stars</span>
														</div>
													)}

													{/* Show comment if exists */}
													{(interaction?.comment || interaction?.description) && (
														<div className="mt-2 p-2 bg-light rounded border-start border-primary border-3">
															<div className="text-muted small mb-1 fw-semibold">User Comment:</div>
															<div className="small">{interaction?.comment || interaction?.description}</div>
														</div>
													)}
												</div>
											</div>
										</div>
									</ListItem>
								))}
							</List>
						</div>
					) : (
						<Typography className="text-center text-muted">No interactions yet</Typography>
					)}
					<div className="text-end mt-3">
						<Button onClick={() => setLikesModalOpen(false)} variant="outlined">
							Close
						</Button>
					</div>
				</Box>
			</Modal>

			<Modal open={false} onClose={() => setUpliftsModalOpen(false)} aria-labelledby="uplifts-modal-title">
				{/* <Modal open={upliftsModalOpen} onClose={() => setUpliftsModalOpen(false)} aria-labelledby="uplifts-modal-title"> */}
				<Box sx={modalStyle}>
					<Typography id="uplifts-modal-title" variant="h6" component="h2" className="mb-3">
						{modalTitle}
					</Typography>

					{/* Original Content Preview */}
					{selectedActivity && (
						<Card className="mb-3" variant="outlined">
							<CardHeader title="Original Rating" subheader="Being Uplifted" size="small" />
							<CardContent>
								<div className="d-flex align-items-center mb-2">
									<Rating value={selectedActivity?.stars} precision={0.5} readOnly size="small" />
									<span className="ms-2 fw-bold">{selectedActivity?.stars}/5</span>
								</div>
								<div className="text-muted small">
									Client: {selectedActivity?.client_name} | Job Date: {formatDate(selectedActivity?.job_date)}
								</div>
								{selectedActivity?.job_issues && selectedActivity?.job_issues.length > 0 && (
									<div className="mt-2">
										<div className="text-muted small mb-1">Issues:</div>
										<div className="d-flex flex-wrap gap-1">
											{selectedActivity.job_issues.slice(0, 3).map((issue, index) => (
												<Chip key={index} label={formatJobIssue(issue)} size="small" color="warning" />
											))}
											{selectedActivity.job_issues.length > 3 && <Chip label={`+${selectedActivity.job_issues.length - 3} more`} size="small" variant="outlined" />}
										</div>
									</div>
								)}
							</CardContent>
						</Card>
					)}

					{/* Uplifts List with Time and Comments */}
					{selectedUplifts.length > 0 ? (
						<div>
							<Typography variant="subtitle1" className="mb-3 fw-bold">
								🚀 Uplifts ({selectedUplifts.length})
							</Typography>
							<List>
								{selectedUplifts.map((uplift, index) => (
									<ListItem key={index} className="px-0 mb-2">
										<div className="w-100">
											<div className="d-flex align-items-start">
												<div className="me-3">
													<span className="text-success fw-bold fs-4">🚀</span>
												</div>
												<div className="flex-grow-1">
													<div className="d-flex justify-content-between align-items-center mb-1">
														<div>
															<span className="fw-bold">{`${uplift?.user?.first_name} ${uplift?.user?.last_name}`}</span>
															{uplift?.user?.verified && <Chip label="Verified" size="small" color="success" className="ms-2" />}
														</div>
														<div className="text-muted small">{formatDateTime(uplift?.createdAt)}</div>
													</div>
													<div className="text-muted small mb-2">{uplift?.user?.email}</div>

													{/* Uplift Rating */}
													<div className="d-flex align-items-center gap-2 mb-2">
														<Rating value={uplift?.stars} precision={0.5} readOnly size="small" />
														<span className="fw-bold">{uplift?.stars}/5 stars</span>
													</div>

													{/* Uplift Issues */}
													{uplift?.job_issues && uplift?.job_issues.length > 0 && (
														<div className="mb-2">
															<div className="text-muted small mb-1">Issues:</div>
															<div className="d-flex flex-wrap gap-1">
																{uplift.job_issues.slice(0, 3).map((issue, issueIndex) => (
																	<Chip key={issueIndex} label={formatJobIssue(issue)} size="small" color="warning" />
																))}
																{uplift.job_issues.length > 3 && <Chip label={`+${uplift.job_issues.length - 3} more`} size="small" variant="outlined" />}
															</div>
														</div>
													)}

													{/* Show uplift comment if exists */}
													{uplift?.description && (
														<div className="mt-2 p-2 bg-light rounded border-start border-success border-3">
															<div className="text-muted small mb-1 fw-semibold">💬 Uplift Comment:</div>
															<div className="small">{uplift.description}</div>
														</div>
													)}
												</div>
											</div>
										</div>
									</ListItem>
								))}
							</List>
						</div>
					) : (
						<Typography className="text-center text-muted">No uplifts yet</Typography>
					)}
					<div className="text-end mt-3">
						<Button onClick={() => setUpliftsModalOpen(false)} variant="outlined">
							Close
						</Button>
					</div>
				</Box>
			</Modal>
		</div>
	);
};

export default MyActivity;
